import { findAugmentingPath } from "./findAugmentingPath";
import type {
  CollectGreedyMatches,
  MatchCandidate,
  MatchPairing,
} from "./types";

const compareCandidates = (
  firstCandidate: MatchCandidate,
  secondCandidate: MatchCandidate,
): number => {
  if (firstCandidate.distance !== secondCandidate.distance) {
    return firstCandidate.distance - secondCandidate.distance;
  }
  const beforeIdOrder = firstCandidate.beforeId.localeCompare(
    secondCandidate.beforeId,
  );
  if (beforeIdOrder !== 0) return beforeIdOrder;
  return firstCandidate.afterId.localeCompare(secondCandidate.afterId);
};

const groupCandidatesByBeforeIndex = (
  sortedCandidates: MatchCandidate[],
): Map<number, MatchCandidate[]> => {
  const candidatesByBeforeIndex = new Map<number, MatchCandidate[]>();
  for (const candidate of sortedCandidates) {
    const beforeCandidates = candidatesByBeforeIndex.get(candidate.beforeIndex);
    if (beforeCandidates === undefined) {
      candidatesByBeforeIndex.set(candidate.beforeIndex, [candidate]);
      continue;
    }
    beforeCandidates.push(candidate);
  }
  return candidatesByBeforeIndex;
};

const buildGreedyPairing = (
  sortedCandidates: MatchCandidate[],
): MatchPairing => {
  const pairing: MatchPairing = {
    pairByBeforeIndex: new Map(),
    pairByAfterIndex: new Map(),
  };
  for (const candidate of sortedCandidates) {
    if (pairing.pairByBeforeIndex.has(candidate.beforeIndex)) continue;
    if (pairing.pairByAfterIndex.has(candidate.afterIndex)) continue;
    pairing.pairByBeforeIndex.set(candidate.beforeIndex, candidate);
    pairing.pairByAfterIndex.set(candidate.afterIndex, candidate);
  }
  return pairing;
};

// Closest pairs win; ids break distance ties so reruns always match identically.
export const collectGreedyMatches: CollectGreedyMatches = (candidates) => {
  const sortedCandidates = [...candidates].sort(compareCandidates);
  const candidatesByBeforeIndex =
    groupCandidatesByBeforeIndex(sortedCandidates);
  const pairing = buildGreedyPairing(sortedCandidates);

  const beforeIndicesInOrder = [...candidatesByBeforeIndex.keys()].sort(
    (firstIndex, secondIndex) => firstIndex - secondIndex,
  );

  for (const beforeIndex of beforeIndicesInOrder) {
    if (pairing.pairByBeforeIndex.has(beforeIndex)) continue;
    findAugmentingPath({
      startBeforeIndex: beforeIndex,
      candidatesByBeforeIndex,
      pairing,
    });
  }

  const pairs: MatchCandidate[] = [];
  for (const beforeIndex of beforeIndicesInOrder) {
    const pair = pairing.pairByBeforeIndex.get(beforeIndex);
    if (pair === undefined) continue;
    pairs.push(pair);
  }

  return {
    pairs,
    matchedBeforeIndices: new Set(pairing.pairByBeforeIndex.keys()),
    matchedAfterIndices: new Set(pairing.pairByAfterIndex.keys()),
  };
};
